import { Suspense, lazy, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  Banknote,
  Boxes,
  FlaskConical,
  MessageSquareWarning,
  PackageX,
  Receipt,
  ShoppingBag,
  Wallet,
  Wifi,
  type LucideIcon,
} from 'lucide-react';
import type { MoneyAmount, OverviewReport } from '@optiflow/types';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';
import { formatMoney } from '@/modules/catalog/money';
import { useLocale } from '@/hooks/useLocale';
import { cn } from '@/utils/cn';
import { ChangeText } from './ChangeText';
import { MoneyByCurrencyText } from './MoneyByCurrencyText';

const NetSalesTrendChart = lazy(() => import('./NetSalesTrendChart'));

/** The base-currency change of a flow figure; other currencies carry their own in the tooltip-free list. */
function baseChange(
  change: { currencyCode: string; pctBasisPoints: number | null }[] | null,
  base: string,
) {
  if (!change) return undefined;
  return change.find((entry) => entry.currencyCode === base)?.pctBasisPoints ?? null;
}

function KpiTile({
  icon: Icon,
  label,
  amounts,
  count,
  change,
  invert,
  hint,
}: {
  icon: LucideIcon;
  label: string;
  amounts?: MoneyAmount[];
  count?: number;
  change?: number | null;
  invert?: boolean;
  hint?: string;
}) {
  const { t, i18n } = useTranslation();
  return (
    <Card className="flex min-w-0 flex-col gap-2 p-4">
      <div className="flex items-start justify-between gap-2">
        <p className="text-muted text-xs font-medium">{label}</p>
        <span className="bg-brand-50 text-brand-600 grid size-8 shrink-0 place-items-center rounded-lg">
          <Icon className="size-4" aria-hidden />
        </span>
      </div>
      {amounts ? (
        <MoneyByCurrencyText amounts={amounts} className="text-ink text-xl font-semibold" />
      ) : (
        <p className="text-ink text-xl font-semibold" dir="ltr">
          {new Intl.NumberFormat(i18n.language === 'ar' ? 'ar-EG' : 'en-GB').format(count ?? 0)}
        </p>
      )}
      {/* More than one currency is worth saying out loud: they are never added together. */}
      {amounts && amounts.length > 1 ? (
        <p className="text-xs text-amber-800">{t('reports.mixedCurrencies')}</p>
      ) : null}
      {change !== undefined ? <ChangeText basisPoints={change} invert={invert} /> : null}
      {hint ? <p className="text-muted text-xs">{hint}</p> : null}
    </Card>
  );
}

function ExceptionRow({
  icon: Icon,
  label,
  value,
  tone,
  to,
}: {
  icon: LucideIcon;
  label: string;
  value: ReactNode;
  tone: 'danger' | 'warning' | 'neutral';
  to?: string;
}) {
  const content = (
    <span className="flex items-center gap-3">
      <span
        className={cn(
          'grid size-8 shrink-0 place-items-center rounded-lg',
          tone === 'danger' && 'bg-red-50 text-red-600',
          tone === 'warning' && 'bg-amber-50 text-amber-600',
          tone === 'neutral' && 'bg-canvas text-muted',
        )}
      >
        <Icon className="size-4" aria-hidden />
      </span>
      <span className="text-ink min-w-0 flex-1 text-sm">{label}</span>
      <span className="text-ink shrink-0 text-sm font-semibold">{value}</span>
    </span>
  );
  return (
    <li className="border-line border-b py-2.5 last:border-b-0">
      {to ? (
        <Link to={to} className="hover:bg-canvas -mx-2 block rounded-lg px-2">
          {content}
        </Link>
      ) : (
        content
      )}
    </li>
  );
}

function Count({ value }: { value: number }) {
  const { t } = useTranslation();
  return value === 0 ? (
    <span className="text-muted font-normal">{t('reports.exceptions.none')}</span>
  ) : (
    <span dir="ltr">{value}</span>
  );
}

export function OverviewSkeleton() {
  return (
    <div className="space-y-4" aria-busy="true">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {Array.from({ length: 6 }, (_, index) => (
          <Skeleton key={index} className="h-28 w-full" />
        ))}
      </div>
      <Skeleton className="h-64 w-full" />
    </div>
  );
}

/**
 * The Overview: core figures, the Net Sales trend, what needs attention, and small summaries.
 *
 * Renders only the panels the server returned. A card for a panel the user may not see is not
 * shown as zero — it is not shown at all.
 */
export function OverviewContent({ report }: { report: OverviewReport }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const [asTable, setAsTable] = useState(false);
  const { panels, meta } = report;
  const base = meta.baseCurrencyCode;

  if (meta.panels.length === 0) {
    return (
      <Card>
        <EmptyState title={t('reports.noPanels.title')} description={t('reports.noPanels.body')} />
      </Card>
    );
  }

  const sales = panels.sales;
  const otherTrendCurrencies =
    sales?.trend.series.filter((entry) => entry.currencyCode !== base) ?? [];

  const exceptions: ReactNode[] = [];
  if (panels.optical) {
    exceptions.push(
      <ExceptionRow
        key="ready"
        icon={AlertTriangle}
        tone={panels.optical.readyPastThreshold > 0 ? 'warning' : 'neutral'}
        label={t('reports.exceptions.readyPast', {
          count: panels.optical.readyForCollectionAttentionDays,
        })}
        value={<Count value={panels.optical.readyPastThreshold} />}
        to="/sales/optical-jobs"
      />,
      <ExceptionRow
        key="lab"
        icon={FlaskConical}
        tone={
          panels.optical.labPastThreshold + panels.optical.orderedNotSentPastThreshold > 0
            ? 'warning'
            : 'neutral'
        }
        label={t('reports.exceptions.labPast', {
          lab: panels.optical.labStageAttentionDays,
          ordered: panels.optical.orderedNotSentAttentionDays,
        })}
        value={
          <Count
            value={panels.optical.labPastThreshold + panels.optical.orderedNotSentPastThreshold}
          />
        }
        to="/sales/optical-jobs"
      />,
    );
  }
  if (panels.receivables) {
    const overdue = panels.receivables.overdueInstalments;
    exceptions.push(
      <ExceptionRow
        key="instalments"
        icon={Receipt}
        tone={overdue.count > 0 ? 'danger' : 'neutral'}
        label={t('reports.exceptions.overdueInstalments', { count: overdue.count })}
        value={
          overdue.count > 0 ? <MoneyByCurrencyText amounts={overdue.amount} /> : <Count value={0} />
        }
        to="/sales/receivables"
      />,
    );
  }
  if (panels.payables) {
    const overdue = panels.payables.overdueInvoices;
    exceptions.push(
      <ExceptionRow
        key="invoices"
        icon={Wallet}
        tone={overdue.count > 0 ? 'danger' : 'neutral'}
        label={t('reports.exceptions.overdueInvoices', { count: overdue.count })}
        value={
          overdue.count > 0 ? <MoneyByCurrencyText amounts={overdue.amount} /> : <Count value={0} />
        }
        to="/finance/payables/invoices?due=OVERDUE"
      />,
    );
  }
  if (panels.inventory) {
    exceptions.push(
      <ExceptionRow
        key="stock"
        icon={PackageX}
        tone={
          panels.inventory.outOfStockLines > 0
            ? 'danger'
            : panels.inventory.lowStockLines > 0
              ? 'warning'
              : 'neutral'
        }
        label={t('reports.exceptions.stock', {
          low: panels.inventory.lowStockLines,
          out: panels.inventory.outOfStockLines,
        })}
        value={<Count value={panels.inventory.lowStockLines + panels.inventory.outOfStockLines} />}
        to="/inventory/stock"
      />,
    );
  }
  if (panels.crm) {
    exceptions.push(
      <ExceptionRow
        key="crm"
        icon={MessageSquareWarning}
        tone={panels.crm.failed + panels.crm.outcomeUnknown > 0 ? 'danger' : 'neutral'}
        label={t('reports.exceptions.failedMessages', {
          failed: panels.crm.failed,
          unknown: panels.crm.outcomeUnknown,
        })}
        value={<Count value={panels.crm.failed + panels.crm.outcomeUnknown} />}
        to="/crm/notifications"
      />,
    );
  }
  if (panels.whatsapp) {
    exceptions.push(
      <ExceptionRow
        key="whatsapp"
        icon={Wifi}
        tone={panels.whatsapp.automationUnavailableBranches > 0 ? 'danger' : 'neutral'}
        label={t('reports.exceptions.whatsapp')}
        value={<Count value={panels.whatsapp.automationUnavailableBranches} />}
        to="/settings/whatsapp"
      />,
    );
  }

  return (
    <div className="space-y-5">
      <section aria-labelledby="reports-core" className="space-y-3">
        <h2 id="reports-core" className="text-ink text-sm font-semibold">
          {t('reports.sections.core')}
        </h2>
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {sales ? (
            <>
              <KpiTile
                icon={Banknote}
                label={t('reports.kpis.netSales')}
                amounts={sales.netSales.current}
                change={baseChange(sales.netSales.change, base)}
              />
              <KpiTile
                icon={ShoppingBag}
                label={t('reports.kpis.confirmedOrders')}
                count={sales.confirmedOrders.current}
                change={
                  sales.confirmedOrders.change
                    ? sales.confirmedOrders.change.pctBasisPoints
                    : undefined
                }
              />
            </>
          ) : null}
          {panels.collections ? (
            <KpiTile
              icon={Wallet}
              label={t('reports.kpis.collections')}
              amounts={panels.collections.collections.current}
              change={baseChange(panels.collections.collections.change, base)}
            />
          ) : null}
          {panels.expenses ? (
            <KpiTile
              icon={Receipt}
              label={t('reports.kpis.expenses')}
              amounts={panels.expenses.expenses.current}
              change={baseChange(panels.expenses.expenses.change, base)}
              invert
            />
          ) : null}
          {panels.receivables ? (
            <KpiTile
              icon={Receipt}
              label={t('reports.kpis.receivables')}
              amounts={panels.receivables.outstanding}
              hint={t('reports.asOfNow')}
            />
          ) : null}
          {panels.payables ? (
            <KpiTile
              icon={Wallet}
              label={t('reports.kpis.payables')}
              amounts={panels.payables.outstanding}
              hint={t('reports.asOfNow')}
            />
          ) : null}
        </div>
      </section>

      {sales ? (
        <Card>
          <CardHeader
            title={t('reports.trend.title')}
            subtitle={t(`reports.granularity.${sales.trend.granularity}`)}
            action={
              <Button variant="ghost" size="sm" onClick={() => setAsTable((value) => !value)}>
                {asTable ? t('reports.trend.viewChart') : t('reports.trend.viewTable')}
              </Button>
            }
          />
          <div className="px-3 pb-4 sm:px-5">
            {asTable ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-muted text-start text-xs">
                      <th className="py-1 text-start font-medium">{t('reports.trend.period')}</th>
                      <th className="py-1 text-end font-medium">{t('reports.trend.current')}</th>
                      <th className="py-1 text-end font-medium">{t('reports.trend.previous')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sales.trend.buckets.map((bucket, index) => {
                      const series = sales.trend.series.find(
                        (entry) => entry.currencyCode === base,
                      );
                      const previous = series?.previous?.[index];
                      return (
                        <tr key={bucket.from} className="border-line border-t">
                          <td className="py-1.5" dir="ltr">
                            {bucket.from === bucket.to
                              ? bucket.from
                              : `${bucket.from} – ${bucket.to}`}
                          </td>
                          <td className="py-1.5 text-end" dir="ltr">
                            {formatMoney(series?.current[index] ?? 0, locale, base)}
                          </td>
                          <td className="text-muted py-1.5 text-end" dir="ltr">
                            {previous === undefined || previous === null
                              ? '—'
                              : formatMoney(previous, locale, base)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <Suspense fallback={<Skeleton className="h-56 w-full" />}>
                <NetSalesTrendChart trend={sales.trend} baseCurrencyCode={base} />
              </Suspense>
            )}
            {otherTrendCurrencies.length > 0 ? (
              <p className="text-muted mt-2 text-xs">
                {t('reports.trend.otherCurrencies')}{' '}
                {otherTrendCurrencies.map((entry) => (
                  <span key={entry.currencyCode} className="ms-2 inline-block" dir="ltr">
                    {formatMoney(
                      entry.current.reduce((sum, value) => sum + value, 0),
                      locale,
                      entry.currencyCode,
                    )}
                  </span>
                ))}
              </p>
            ) : null}
          </div>
        </Card>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-2">
        {exceptions.length > 0 ? (
          <Card>
            <CardHeader title={t('reports.sections.exceptions')} subtitle={t('reports.asOfNow')} />
            <ul className="px-5 pb-3">{exceptions}</ul>
          </Card>
        ) : null}

        {panels.optical ? (
          <Card>
            <CardHeader title={t('reports.sections.pipeline')} subtitle={t('reports.asOfNow')} />
            <ul className="grid grid-cols-2 gap-2 px-5 pb-5 sm:grid-cols-3">
              {(Object.entries(panels.optical.pipeline) as [string, number][]).map(
                ([status, count]) => (
                  <li key={status} className="border-line rounded-lg border px-3 py-2">
                    <p className="text-ink text-lg font-semibold" dir="ltr">
                      {count}
                    </p>
                    <p className="text-muted text-xs">{t(`sales.opticalJobs.status.${status}`)}</p>
                  </li>
                ),
              )}
            </ul>
          </Card>
        ) : null}

        {panels.inventory || panels.inventoryValue ? (
          <Card>
            <CardHeader title={t('reports.sections.stockHealth')} subtitle={t('reports.asOfNow')} />
            <div className="space-y-3 px-5 pb-5">
              {panels.inventoryValue ? (
                <div className="space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-muted text-xs font-medium">
                      {t('reports.kpis.estimatedValue')}
                    </p>
                    {panels.inventoryValue.partialEstimate ? (
                      <Badge tone="warning">{t('reports.partialEstimate')}</Badge>
                    ) : null}
                  </div>
                  <MoneyByCurrencyText
                    amounts={panels.inventoryValue.estimatedValue}
                    className="text-ink text-xl font-semibold"
                  />
                  <p className="text-muted text-xs">
                    {panels.inventoryValue.lineCoverageBasisPoints === null
                      ? t('reports.coverage.noStock')
                      : t('reports.coverage.lines', {
                          percent: new Intl.NumberFormat(locale === 'ar' ? 'ar-EG' : 'en-GB', {
                            maximumFractionDigits: 1,
                          }).format(panels.inventoryValue.lineCoverageBasisPoints / 100),
                          costed: panels.inventoryValue.costedLineCount,
                          total: panels.inventoryValue.totalOnHandLineCount,
                        })}
                  </p>
                </div>
              ) : null}
              {panels.inventory ? (
                <ul className="grid grid-cols-3 gap-2">
                  <li className="border-line rounded-lg border px-3 py-2">
                    <p className="text-ink text-lg font-semibold" dir="ltr">
                      {panels.inventory.slowMovingLines}
                    </p>
                    <p className="text-muted text-xs">
                      {t('reports.stagnation.slow', { days: panels.inventory.slowMovingDays })}
                    </p>
                  </li>
                  <li className="border-line rounded-lg border px-3 py-2">
                    <p className="text-ink text-lg font-semibold" dir="ltr">
                      {panels.inventory.deadStockLines}
                    </p>
                    <p className="text-muted text-xs">
                      {t('reports.stagnation.dead', { days: panels.inventory.deadStockDays })}
                    </p>
                  </li>
                  <li className="border-line rounded-lg border px-3 py-2">
                    <p className="text-ink text-lg font-semibold" dir="ltr">
                      {panels.inventory.unknownMovementLines}
                    </p>
                    <p className="text-muted text-xs">{t('reports.stagnation.unknown')}</p>
                  </li>
                </ul>
              ) : null}
            </div>
          </Card>
        ) : null}

        {sales && sales.byBranch.length > 1 ? (
          <Card>
            <CardHeader title={t('reports.sections.byBranch')} />
            <ul className="px-5 pb-3">
              {sales.byBranch.map((row) => (
                <li
                  key={row.branchId}
                  className="border-line flex items-start justify-between gap-3 border-b py-2.5 last:border-b-0"
                >
                  <span className="text-ink flex items-center gap-2 text-sm">
                    <Boxes className="text-muted size-4" aria-hidden />
                    <span dir="auto">{row.branchName}</span>
                  </span>
                  <MoneyByCurrencyText
                    amounts={row.netSales}
                    className="text-ink text-sm font-semibold"
                  />
                </li>
              ))}
            </ul>
          </Card>
        ) : null}
      </div>
    </div>
  );
}
